import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import {
  AdminSurveyRequestSummary,
  DerinOrganization,
  deleteSurveyRequest,
  getSurveyRequestMarkdown,
  getSurveyRequestsByOrganization,
} from "../../../api/derin";
import { useToast } from "../Toast";
import { errorMessageOf } from "../errorMessage";
import {
  collectionColors,
  font,
  outlinedButtonSx,
  subtitleSx,
} from "../CollectionsTab/tokens";
import DeleteSurveyRequestDialog from "./DeleteSurveyRequestDialog";
import SurveyRequestFilesDialog from "./SurveyRequestFilesDialog";
import { formatUtcDate, formatUtcTime, saveBlob } from "./format";

interface SurveyRequestsTabProps {
  organization: DerinOrganization;
  /** Null until the first load finishes, so callers don't show a count of 0. */
  onCountChange?: (count: number | null) => void;
}

type SortDirection = "asc" | "desc";

const headerCellSx = {
  fontFamily: font,
  fontWeight: 500,
  fontSize: "14px",
  lineHeight: "20px",
  letterSpacing: "-0.150391px",
  color: collectionColors.textPrimary,
  borderBottom: `1px solid ${collectionColors.border}`,
  padding: "10px 8px",
};

const bodyCellSx = {
  borderBottom: `1px solid ${collectionColors.border}`,
  padding: "14px 8px",
  verticalAlign: "middle" as const,
};

const primaryTextSx = {
  fontFamily: font,
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "20px",
  letterSpacing: "-0.150391px",
  color: collectionColors.textPrimary,
};

const secondaryTextSx = {
  ...subtitleSx,
  fontSize: "12px",
  lineHeight: "16px",
};

// Compact row actions, as in the Figma: 32px tall, 12px side padding, 6px icon gap.
const actionButtonSx = {
  ...outlinedButtonSx,
  minWidth: 0,
  padding: "6px 12px",
  gap: "6px",
  "& .MuiButton-startIcon": { margin: 0 },
};

const deleteButtonSx = {
  ...actionButtonSx,
  background: collectionColors.dangerSurface,
  border: "1px solid transparent",
  color: collectionColors.danger,
  fontSize: "13px",
  "&:hover": { background: collectionColors.dangerSurfaceHover },
};

// Column widths from the Figma; Actions takes the rest and is right-aligned.
const columnWidths = {
  request: "322px",
  submittedBy: "200px",
  submitted: "130px",
};

/**
 * Briefs the organization submitted from the intake wizard in the client app. The portal
 * only reads them: download the brief (Markdown) and any attached files, or delete one.
 */
const SurveyRequestsTab: React.FC<SurveyRequestsTabProps> = ({
  organization,
  onCountChange,
}) => {
  const { showSuccess, showError } = useToast();

  const [requests, setRequests] = useState<AdminSurveyRequestSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [filesFor, setFilesFor] = useState<AdminSurveyRequestSummary | null>(null);
  const [deleteFor, setDeleteFor] = useState<AdminSurveyRequestSummary | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchRequests = useCallback(async () => {
    setIsLoading(true);
    try {
      setRequests(await getSurveyRequestsByOrganization(organization.id));
    } catch (err) {
      console.error("Error fetching survey requests:", err);
      showError(`Error fetching survey requests: ${errorMessageOf(err)}`);
    } finally {
      setIsLoading(false);
      setHasLoaded(true);
    }
  }, [organization.id, showError]);

  useEffect(() => {
    void fetchRequests();
  }, [fetchRequests]);

  useEffect(() => {
    onCountChange?.(hasLoaded ? requests.length : null);
  }, [requests.length, hasLoaded, onCountChange]);

  const sortedRequests = useMemo(() => {
    const sign = sortDirection === "desc" ? -1 : 1;
    return [...requests].sort(
      (a, b) =>
        sign * (new Date(a.submittedAt).getTime() - new Date(b.submittedAt).getTime())
    );
  }, [requests, sortDirection]);

  const handleDownloadMarkdown = async (request: AdminSurveyRequestSummary) => {
    setDownloadingId(request.id);
    try {
      const { fileName, markdown } = await getSurveyRequestMarkdown(
        organization.id,
        request.id
      );
      saveBlob(new Blob([markdown], { type: "text/markdown" }), fileName);
    } catch (err) {
      console.error("Error downloading survey request:", err);
      showError(`Error downloading survey request: ${errorMessageOf(err)}`);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteFor) return;
    setIsDeleting(true);
    try {
      await deleteSurveyRequest(organization.id, deleteFor.id);
      showSuccess("Survey request deleted");
      setDeleteFor(null);
      await fetchRequests();
    } catch (err) {
      console.error("Error deleting survey request:", err);
      showError(`Error deleting survey request: ${errorMessageOf(err)}`);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Box
      sx={{
        boxSizing: "border-box",
        width: "100%",
        background: collectionColors.white,
        border: `1px solid ${collectionColors.border}`,
        borderRadius: "14px",
      }}
    >
      {/* Card header */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          padding: "0px 24px",
          height: "80px",
        }}
      >
        <Typography
          sx={{
            fontFamily: font,
            fontWeight: 500,
            fontSize: "18px",
            lineHeight: "27px",
            color: collectionColors.textPrimary,
          }}
        >
          Survey Requests
        </Typography>
        <Typography sx={{ ...subtitleSx, fontSize: "16px", lineHeight: "24px" }}>
          View submitted briefs from {organization.name} and download them for survey design.
        </Typography>
      </Box>

      <Box sx={{ padding: "0px 24px 24px" }}>
        {isLoading && !hasLoaded ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : requests.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <Typography sx={subtitleSx}>No survey requests yet</Typography>
          </Box>
        ) : (
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headerCellSx, width: columnWidths.request }}>Request</TableCell>
                  <TableCell sx={{ ...headerCellSx, width: columnWidths.submittedBy }}>
                    Submitted by
                  </TableCell>
                  <TableCell
                    sx={{ ...headerCellSx, width: columnWidths.submitted }}
                    sortDirection={sortDirection}
                    aria-sort={sortDirection === "desc" ? "descending" : "ascending"}
                  >
                    <TableSortLabel
                      active
                      direction={sortDirection}
                      onClick={() =>
                        setSortDirection((prev) => (prev === "desc" ? "asc" : "desc"))
                      }
                      sx={{
                        "& .MuiTableSortLabel-icon": {
                          width: 16,
                          height: 16,
                          color: `${collectionColors.textSecondary} !important`,
                        },
                      }}
                    >
                      Submitted
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={headerCellSx} align="right">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedRequests.map((request) => (
                  <TableRow
                    key={request.id}
                    data-testid={`survey-request-row-${request.id}`}
                    sx={{ "&:hover": { background: collectionColors.surfaceLight } }}
                  >
                    <TableCell sx={bodyCellSx}>
                      <Typography
                        sx={{
                          ...primaryTextSx,
                          fontWeight: 500,
                          color: collectionColors.buttonPrimary,
                        }}
                      >
                        {request.title}
                      </Typography>
                      <Typography sx={secondaryTextSx}>Survey brief</Typography>
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <Typography sx={primaryTextSx}>{request.submittedBy.name}</Typography>
                      <Typography sx={secondaryTextSx}>{request.submittedBy.email}</Typography>
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <Typography sx={primaryTextSx}>
                        {formatUtcDate(request.submittedAt)}
                      </Typography>
                      <Typography sx={secondaryTextSx}>
                        {formatUtcTime(request.submittedAt)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ ...bodyCellSx, whiteSpace: "nowrap" }} align="right">
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          gap: "4px",
                        }}
                      >
                        {/* Files sits first so Download .md and Delete keep the same
                            right-anchored positions in every row. */}
                        {request.files.length > 0 && (
                          <Button
                            aria-label={`Files of ${request.title}`}
                            onClick={() => setFilesFor(request)}
                            startIcon={<AttachFileIcon sx={{ width: 16, height: 16 }} />}
                            sx={actionButtonSx}
                          >
                            Files ({request.files.length})
                          </Button>
                        )}
                        <Button
                          aria-label={`Download ${request.title} as Markdown`}
                          onClick={() => void handleDownloadMarkdown(request)}
                          disabled={downloadingId === request.id}
                          startIcon={
                            // 16px like the icon it replaces, so the button keeps its width.
                            downloadingId === request.id ? (
                              <CircularProgress size={16} />
                            ) : (
                              <FileDownloadOutlinedIcon sx={{ width: 16, height: 16 }} />
                            )
                          }
                          sx={actionButtonSx}
                        >
                          Download .md
                        </Button>
                        <Button
                          aria-label={`Delete ${request.title}`}
                          onClick={() => setDeleteFor(request)}
                          startIcon={<DeleteOutlineIcon sx={{ width: 16, height: 16 }} />}
                          sx={deleteButtonSx}
                        >
                          Delete
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <SurveyRequestFilesDialog
        open={Boolean(filesFor)}
        organizationId={organization.id}
        request={filesFor}
        onClose={() => setFilesFor(null)}
      />

      <DeleteSurveyRequestDialog
        open={Boolean(deleteFor)}
        request={deleteFor}
        organizationName={organization.name}
        onClose={() => setDeleteFor(null)}
        onConfirm={() => void handleConfirmDelete()}
        isSubmitting={isDeleting}
      />
    </Box>
  );
};

export default SurveyRequestsTab;
